/* Reserva de cita dentro de la ficha de producto (PDP) — Fixel.
 *
 * Qué hace: pone dos botones bajo el selector de reparación ("Agendar cita" y
 * "Recogida y entrega") y abre el asistente de `fixel-reserva.js` en una
 * ventana, sin salir de la ficha y sin repetir el modelo ni la reparación, que
 * ya vienen elegidos ahí.
 *
 * Por qué así y no dentro de la ficha directamente:
 *   Las fichas de reparación las pinta el maquetador **Instant**
 *   (`sections/instant-*.liquid`, generadas por la app). Tocar esos ficheros no
 *   sirve: la app los reescribe cada vez que se publica un diseño. Por eso este
 *   script vive en el tema, se carga desde `theme.liquid` y trabaja sobre el DOM
 *   que Instant haya pintado. Sobrevive a cualquier republicación.
 *
 * De dónde saca las reparaciones elegidas, en este orden:
 *   1. La ficha nueva del tema Mode (`sections/fixel-repair-product.liquid`):
 *      `[data-fxrp-service]` con `data-qty > 0`. Ahí se pueden elegir **varias**
 *      reparaciones con cantidad, y se llevan todas.
 *   2. La ficha actual, maquetada con Instant: el `<select>` de
 *      `[data-instant-type="VARIANT_PICKER"]`, una sola reparación.
 *   En los dos casos se leen los atributos del HTML visible, no las variables
 *   internas de la app o de la sección, que no son contrato de nadie. Si no se
 *   encuentra nada, el asistente pide la reparación como en la página de
 *   reservas: se degrada, no se rompe.
 *
 * También se puede abrir desde cualquier enlace o botón del maquetador poniendo
 * como destino `#fixel-cita` o `#fixel-recogida`: así Miguel puede colocar los
 * botones donde quiera desde Instant sin tocar código.
 */
(function () {
  'use strict';

  function init() {
    var popup = document.querySelector('[data-fxr-popup]');
    if (!popup || popup.dataset.fxrListo) return;
    popup.dataset.fxrListo = '1';

    var root = popup.querySelector('[data-fxr-root]');
    var panel = popup.querySelector('[data-fxr-panel]');
    var handle = (popup.dataset.fxrHandle || '').trim();
    var servicio = ((root && root.dataset.servicioVariante) || '').trim();
    var ultimoFoco = null;

    // ------------------------------------------------------------- la ventana

    function abre(flujo) {
      ultimoFoco = document.activeElement;
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      // El scroll del fondo se bloquea, pero se guarda la posición: sin esto,
      // al cerrar la ficha vuelve arriba y el cliente pierde dónde estaba.
      document.body.style.top = -window.scrollY + 'px';
      document.body.classList.add('fxr-bloqueado');
      // El navegador tiene que haber pintado la ventana antes de mover el foco.
      requestAnimationFrame(function () {
        var cerrar = popup.querySelector('[data-fxr-cerrar]');
        if (cerrar) cerrar.focus();
      });
      if (root && typeof root.fxrAbrir === 'function') {
        root.fxrAbrir({
          handle: handle,
          seleccion: reparacionesElegidas(),
          totalFicha: totalDeLaFicha(),
          flujo: flujo
        });
      }
    }

    function cierra() {
      var y = parseInt(document.body.style.top || '0', 10) * -1;
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('fxr-bloqueado');
      document.body.style.top = '';
      window.scrollTo(0, y || 0);
      if (ultimoFoco && typeof ultimoFoco.focus === 'function') ultimoFoco.focus();
    }

    popup.addEventListener('click', function (e) {
      // Solo cierran la X y el fondo, y se comprueba de forma explícita.
      //
      // Antes se cerraba con "el clic no ha caído dentro del panel", y eso tenía
      // un fallo feo: al pulsar una opción, el asistente repinta el paso y el
      // botón pulsado deja de estar en el documento, así que cuando el clic
      // llegaba hasta aquí `panel.contains(...)` decía que no y la ventana se
      // cerraba sola al continuar. Lo vio Miguel el 12/08.
      if (e.target.closest('[data-fxr-cerrar]') ||
          e.target === popup ||
          e.target.classList.contains('fxr-modal__fondo')) {
        e.preventDefault();
        cierra();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hidden) cierra();
    });

    // El foco no debe escaparse de la ventana mientras está abierta.
    popup.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || popup.hidden || !panel) return;
      var focos = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focos.length) return;
      var primero = focos[0];
      var ultimo = focos[focos.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    });

    // ---------------------------------------- qué reparaciones hay elegidas

    // Devuelve [{varianteId, opcion, qty}]. Vacío = el asistente preguntará.
    function reparacionesElegidas() {
      // 1) Ficha del tema Mode: varias reparaciones con contador por variante.
      var servicios = document.querySelectorAll('[data-fxrp-service]');
      if (servicios.length) {
        var lista = [];
        Array.prototype.forEach.call(servicios, function (s) {
          var qty = parseInt(s.dataset.qty || '0', 10);
          if (!qty || s.getAttribute('data-available') !== 'true') return;
          lista.push({
            varianteId: s.getAttribute('data-variant-id'),
            opcion: (s.getAttribute('data-title') || '').trim(),
            qty: qty
          });
        });
        return lista;
      }

      // 2) Ficha maquetada con Instant: un <select> con el nombre de cada
      //    reparación. Es lo que el cliente acaba de tocar.
      var selects = document.querySelectorAll('[data-instant-type="VARIANT_PICKER"] select');
      for (var i = 0; i < selects.length; i++) {
        var v = (selects[i].value || '').trim();
        if (v) return [{ opcion: v, qty: 1 }];
      }

      // 3) Variantes pintadas como botones o radios.
      var marcado = document.querySelector(
        '[data-instant-type="VARIANT_PICKER"] input[type="radio"]:checked, ' +
        '.fxl-card.is-selected[data-option-value]'
      );
      if (marcado) {
        var valor = (marcado.value || marcado.dataset.optionValue || '').trim();
        if (valor) return [{ opcion: valor, qty: 1 }];
      }
      return [];
    }

    // El total que la ficha está enseñando, ya con sus promociones aplicadas
    // (hoy los 15 € de la batería con otra avería). Se lee su número en vez de
    // repetir aquí la regla: si mañana cambian el descuento o añaden otro, la
    // ventana sigue diciendo lo mismo que la ficha sin tocar nada.
    // Devuelve null si no se encuentra, y entonces la ventana suma como siempre.
    function totalDeLaFicha() {
      var el = document.querySelector(
        '[data-fxrp-panel-total], [data-fxrp-total], [data-fxrp-sticky-total]'
      );
      if (!el) return null;
      return aNumero(el.textContent);
    }

    // Los importes vienen con el formato de la tienda: "487,00 €", "€1.234,56",
    // "1,234.56 €"... Se queda con la última coma o punto como separador de
    // decimales, que es lo único que distingue de verdad los dos formatos.
    function aNumero(texto) {
      var limpio = String(texto || '').replace(/[^0-9.,]/g, '');
      if (!limpio) return null;
      var corte = Math.max(limpio.lastIndexOf(','), limpio.lastIndexOf('.'));
      var entero, decimales;
      if (corte === -1 || limpio.length - corte > 3) {
        entero = limpio;
        decimales = '';
      } else {
        entero = limpio.slice(0, corte);
        decimales = limpio.slice(corte + 1);
      }
      var n = parseFloat(entero.replace(/[.,]/g, '') + (decimales ? '.' + decimales : ''));
      return isNaN(n) ? null : n;
    }

    // "Cambiar las reparaciones" desde dentro de la ventana: se cierra y se
    // lleva al cliente al selector de la ficha, que es donde están las
    // categorías, los tiempos y las promociones. Responde a la petición de
    // Miguel de poder añadir otra reparación sin empezar de cero.
    function volverAlSelector() {
      cierra();
      var destino = document.querySelector(
        '[data-fxrp-service-list], [data-instant-type="VARIANT_PICKER"], .fxl-selector'
      );
      if (!destino) return;
      // Primero se señala y luego se intenta el scroll: si el navegador no
      // soporta `scrollIntoView` con opciones, el cliente ve igualmente dónde
      // tiene que mirar.
      destino.classList.add('fxr-destacado');
      setTimeout(function () { destino.classList.remove('fxr-destacado'); }, 1600);
      if (typeof destino.scrollIntoView !== 'function') return;
      try {
        destino.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        destino.scrollIntoView();
      }
    }

    popup.addEventListener('fxr:volver-a-reparaciones', volverAlSelector);

    // ------------------------------------------------------------ los botones

    function boton(flujo, texto, principal) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fxr-cta__btn' + (principal ? ' fxr-cta__btn--primario' : '');
      b.textContent = texto;
      b.addEventListener('click', function () { abre(flujo); });
      return b;
    }

    // La ficha del tema Mode ya trae sus propios CTA ("Agendar cita" y
    // "Recogida y entrega") con su diseño y su estado desactivado. No se
    // duplican: se les engancha el popup y se les quita la navegación.
    //
    // Los dos oyentes van en `document` y en fase de captura a propósito: así
    // corren antes que los de la sección, que están puestos en el propio botón
    // y en el formulario, y `stopPropagation` evita que además naveguen o
    // manden el carrito.
    // La ficha tiene TRES botones de cita (el principal, el del panel de resumen
    // y el de la barra fija del móvil) y tres de recogida. Hay que enganchar los
    // tres: enganchar solo uno deja los otros dos sin hacer nada.
    var CITA = '[data-fxrp-cita], [data-fxrp-panel-cita], [data-fxrp-sticky-cita]';
    var RECOGIDA = '[data-fxrp-submit], [data-fxrp-panel-submit], [data-fxrp-sticky-btn]';

    function engranaFichaPropia() {
      var citas = document.querySelectorAll(CITA);
      var formulario = document.querySelector('[data-fxrp-form]');
      if (!citas.length && !formulario) return false;

      // Su sección deja el botón de cita permanentemente inerte cuando no hay
      // una URL de agenda configurada en los ajustes, y ya no hace falta
      // ninguna: la ventana la abrimos aquí. Se le marca como si tuviera
      // destino para que su propio código lo active en cuanto haya
      // reparaciones elegidas, y así el botón no se queda apagado.
      // Solo el atributo, nunca un `href`: un enlace a #fixel-cita lo recogería
      // el oyente de los botones puestos a mano, que abre sin comprobar si hay
      // algo elegido, y el CTA abriría la ventana con la ficha vacía.
      Array.prototype.forEach.call(citas, function (b) {
        b.setAttribute('data-has-url', 'true');
      });

      if (citas.length) {
        document.addEventListener('click', function (e) {
          var b = e.target.closest(CITA);
          if (!b) return;
          e.preventDefault();
          // Sin nada elegido el botón no hace nada, igual que en su ficha. No se
          // mira su `aria-disabled` porque lo pone a "true" también cuando solo
          // falta la URL, y entonces el botón nunca abriría la ventana.
          if (!reparacionesElegidas().length) return;
          e.stopPropagation();
          abre('tienda');
        }, true);
      }

      // "Recogida y entrega" abre el asistente de domicilio. Al confirmar se
      // añade al carrito y se va al checkout. Desmarcar el ajuste en el tema
      // vuelve a añadir al carrito en el clic, sin asistente.
      // Los CTA del panel y del sticky son type=button fuera del form: hay que
      // coger el clic, no solo el submit, o el interruptor no los cubre.
      if (popup.dataset.fxrRecogida === '1') {
        document.addEventListener('click', function (e) {
          var b = e.target.closest(RECOGIDA);
          if (!b || b.disabled) return;
          if (!reparacionesElegidas().length) return;
          e.preventDefault();
          e.stopPropagation();
          abre('domicilio');
        }, true);
        if (formulario) {
          document.addEventListener('submit', function (e) {
            if (!e.target.matches('[data-fxrp-form]')) return;
            if (!reparacionesElegidas().length) return;  // su validación manda
            e.preventDefault();
            e.stopPropagation();
            abre('domicilio');
          }, true);
        }
      }
      return true;
    }

    function inyectaBotones() {
      if (popup.dataset.fxrInyectar !== '1') return;
      if (document.querySelector('.fxr-cta')) return;
      // Si la ficha ya tiene sus CTA, no se añaden otros dos.
      if (document.querySelector('[data-fxrp-cita], [data-fxrp-submit]')) return;

      // Debajo del selector de reparación: es donde el cliente acaba de decidir
      // qué necesita y donde tiene sentido preguntarle cómo lo quiere hacer.
      var ancla = document.querySelector('[data-instant-type="VARIANT_PICKER"]');
      if (!ancla) return;

      var caja = document.createElement('div');
      caja.className = 'fxr-cta';
      caja.appendChild(boton('tienda', popup.dataset.fxrCtaCita || 'Agendar cita', true));
      // Sin producto de servicio configurado no hay forma de cobrar la recogida,
      // así que ese botón no se pinta: mejor una vía menos que una vía rota.
      if (servicio) {
        caja.appendChild(boton('domicilio', popup.dataset.fxrCtaRecogida || 'Recogida y entrega', false));
      }
      ancla.parentNode.insertBefore(caja, ancla.nextSibling);
    }

    var conFichaPropia = engranaFichaPropia();
    if (!conFichaPropia) inyectaBotones();
    // Instant monta parte de la ficha por JS: si el selector aún no estaba, se
    // vuelve a intentar cuando aparezca.
    if (!conFichaPropia && !document.querySelector('.fxr-cta') && window.MutationObserver) {
      var observador = new MutationObserver(function () {
        inyectaBotones();
        if (document.querySelector('.fxr-cta')) observador.disconnect();
      });
      observador.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observador.disconnect(); }, 10000);
    }

    // Botones puestos a mano desde el maquetador: enlace a #fixel-cita o
    // #fixel-recogida, o atributo data-fxr-abrir="cita|recogida".
    document.addEventListener('click', function (e) {
      var disparador = e.target.closest(
        'a[href*="#fixel-cita"], a[href*="#fixel-recogida"], [data-fxr-abrir]'
      );
      if (!disparador || popup.contains(disparador)) return;
      // Los CTA de la propia ficha tienen su oyente, que además comprueba que
      // haya reparaciones elegidas. Si alguno lleva también uno de estos
      // enlaces, manda el suyo.
      if (disparador.matches(CITA) || disparador.matches(RECOGIDA)) return;
      var quiere = disparador.dataset.fxrAbrir || (disparador.getAttribute('href') || '');
      e.preventDefault();
      abre(/recogida|domicilio/.test(quiere) ? 'domicilio' : 'tienda');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
